import { teamsService } from '@/services'
import createDefaultTeamProjects from '@/onboarding/createDefaultTeamProjects'

export default {
  state: {
    team: {},
    teamId: null,
    members: null,
    headquarters: {},
    totalMembers: null,
    temporaryMembers: null,
    totalTemporaryMembers: null,
    member: null
  },

  getters: {
    isOwner (state, _, rootState) {
      return state.team?.userId === rootState.profile.user.id
    },

    isAdmin (state) {
      return state.member?.teamRole.name === 'admin'
    },

    isRegularUser (state) {
      return state.member?.teamRole.name === 'user'
    },

    hasTeam (state) {
      return state.team?.id
    },

    isTeamAccount (state) {
      return state.teamId
    },

    trialDaysLeft (state) {
      const now = new Date()
      const createdAtTeamAccount = new Date(state.team?.createdAt)

      const diffTime = now.getTime() - createdAtTeamAccount.getTime()
      const diffDays = diffTime / (1000 * 3600 * 24)

      return 15 - diffDays <= 0 ? 0 : 15 - parseInt(diffDays)
    },

    isTrialOver (state, getters) {
      return getters.isTeamAccount && getters.isTrialPlan && getters.trialDaysLeft <= 0
    },

    isHeadquarters (state) {
      return (id) => {
        return state.headquarters?.id === id
      }
    }
  },

  mutations: {
    setTeam (state, payload) {
      state.team = payload
    },

    setHeadquarters (state, payload) {
      state.headquarters = payload
    },

    setTeamId (state, payload) {
      state.teamId = payload
    },

    setLogo (state, logo) {
      state.team.teamLogo = logo
    },

    removeTeam (state) {
      state.team = {}
      state.teamId = null
      state.members = null
      state.member = null
    },

    setMembers (state, payload) {
      state.members = payload.members
      state.totalMembers = payload.totalMembers
    },

    setTemporaryMembers (state, payload) {
      state.temporaryMembers = payload.members
      state.totalTemporaryMembers = payload.totalMembers
    },

    setMember (state, payload) {
      state.member = payload
    },

    updateMemberRole (state, { memberId, role }) {
      if (state.member.id === memberId) {
        state.member = {
          ...state.member,
          teamRole: { name: role }
        }
      }

      const members = state.members.map(m => {
        if (m.id === memberId) {
          return {
            ...m,
            role: role,
            hasBillingAccess: role === 'admin' ? true : m.hasBillingAccess
          }
        } else {
          return m
        }
      })

      state.members = members
    },

    updateBillingAccess (state, { memberId, billingAccess }) {
      const members = state.members.map(m => {
        if (m.id === memberId) {
          return {
            ...m,
            hasBillingAccess: billingAccess
          }
        } else {
          return m
        }
      })

      state.members = members
    },

    updateTempMemberRole (state, { memberId, role }) {
      const members = state.temporaryMembers.map(m => {
        if (m.id === memberId) {
          return {
            ...m,
            teamRole: { name: role }
          }
        } else {
          return m
        }
      })

      state.temporaryMembers = members
    },

    updateTeamInfo (state, value) {
      state.team.info = value
    }
  },

  actions: {
    async getTeam ({ commit, dispatch }) {
      commit('loading', true)
      try {
        const res = await teamsService.getTeam()

        commit('setTeam', res?.data.team)
        commit('setHeadquarters', res?.data.headquarters)
        commit('setTeamId', res?.data.team?.id)
        if (res?.data.team) dispatch('getMember')

        return Promise.resolve(res?.data.team)
      } catch (e) {
        return Promise.reject(e)
      }
    },

    getMembers ({ commit }, payload) {
      return teamsService.getMembers(payload)
        .then(({ data }) => {
          commit('setMembers', {
            members: data.members,
            totalMembers: data.totalMembers
          })
          return data.members
        })
    },

    getTemporaryMembers ({ commit }, payload) {
      teamsService.getTemporaryMembers(payload)
        .then(({ data }) => {
          commit('setTemporaryMembers', {
            members: data.temporaryMembers,
            totalMembers: data.totalMembers
          })
        })
    },

    getMember ({ commit }) {
      teamsService.getMember().then(({ data }) => {
        commit('setMember', data.member)
      })
    },

    async registerTeam ({ commit, rootState }, payload) {
      commit('loading', true)

      try {
        // create user and set token
        const { data } = await teamsService.register(payload)
        commit('setToken', data.token)
        commit('setTeam', data.team)
        commit('setTeamId', data.team.id)

        const team = await teamsService.getTeam()

        const settings = rootState.settings.settings
        commit('loading', true)

        await createDefaultTeamProjects(commit, settings, team.data.headquarters.id)

        return Promise.resolve(data)
      } catch (err) {
        commit('loading', false)
        return Promise.reject(err)
      }
    },

    async createTeam ({ commit, rootState }, payload) {
      commit('loading', true)

      try {
        const { data } = await teamsService.createTeam(payload)
        commit('setTeam', data.team)
        commit('setTeamId', data.team.id)

        const team = await teamsService.getTeam()

        const settings = rootState.settings.settings
        commit('loading', true)

        await createDefaultTeamProjects(commit, settings, team.data.headquarters.id)

        return Promise.resolve(data)
      } catch (err) {
        commit('loading', false)
        return Promise.reject(err)
      }
    },

    async acceptInvite ({ commit }, payload) {
      commit('loading', true)

      try {
        const { data } = await teamsService.acceptInvite(payload)
        commit('setTeam', data.team)
        commit('setTeamId', data.team.id)

        return Promise.resolve(data)
      } catch (err) {
        commit('loading', false)
        return Promise.reject(err)
      }
    },

    async acceptInviteAndCreateUser ({ commit }, payload) {
      commit('loading', true)

      try {
        const { data } = await teamsService.acceptInviteAndCreateUser(payload)
        commit('setToken', data.token)
        commit('setTeam', data.team)
        commit('setTeamId', data.team.id)

        return Promise.resolve(data)
      } catch (err) {
        commit('loading', false)
        return Promise.reject(err)
      }
    },

    updateMemberRole ({ commit }, { id, role }) {
      teamsService.updateMemberRole({
        roleName: role,
        memberId: id
      }).then(() => {
        commit('updateMemberRole', {
          memberId: id,
          role: role
        })
      })
    },

    updateTempMemberRole ({ commit }, { id, role }) {
      teamsService.updateTempMemberRole({
        roleName: role,
        memberId: id
      }).then(() => {
        commit('updateTempMemberRole', {
          memberId: id,
          role: role
        })
      })
    },

    updateBillingAccess ({ commit }, { id, billingAccess }) {
      teamsService.updateBillingAccess({
        billingAccess: billingAccess,
        memberId: id
      }).then(() => {
        commit('updateBillingAccess', {
          memberId: id,
          billingAccess: billingAccess
        })
      })
    },

    updateTeamInfo ({ commit }, info) {
      teamsService.updateTeamInfo(info)
    }
  }
}
